/**
 * AdminPhone — Admin console for the Twilio phone system.
 * Route: /admin/phone
 *
 * Sections:
 *  • KPI strip: total calls, answer rate, missed, voicemail, SMS
 *  • Time-series bar chart (inbound / outbound / missed per day)
 *  • Per-agent leaderboard
 *  • Recent voicemails with playback + transcript
 *  • Settings drawer: current number, TwiML app SID, account type, "Rewire to production" action
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, MessageSquare, Voicemail,
  Settings, RefreshCw, AlertTriangle, CheckCircle2, Wifi,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatPhoneDisplay, formatDuration } from "@/lib/phone";
import { formatDateTime, relativeTime } from "@/pages/bdc/bdcUi";

const KpiCard = ({ label, value, sub, tone = "slate", icon: Icon, testId }) => {
  const tones = {
    slate: "bg-white text-slate-900 border-slate-200",
    brand: "bg-brand text-white border-brand",
    gold: "bg-[#F7EFD1] text-[#7A5C0E] border-[#F1E1A8]",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose: "bg-rose-50 text-rose-800 border-rose-200",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className={`text-[10px] uppercase tracking-widest font-semibold ${tone === "brand" ? "text-white/70" : "text-slate-500"}`}>{label}</p>
        {Icon && <Icon size={14} className={tone === "brand" ? "text-white/80" : "text-slate-400"} />}
      </div>
      <p className="mt-1.5 font-display text-[24px] font-semibold">{value}</p>
      {sub && <p className={`text-[11px] ${tone === "brand" ? "text-white/70" : "text-slate-500"}`}>{sub}</p>}
    </div>
  );
};

const AdminPhone = () => {
  const [range, setRange] = useState("30d");
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rewireHost, setRewireHost] = useState("");
  const [rewireSaving, setRewireSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get(`/admin/phone/analytics`, { params: { range } }),
        api.get(`/admin/phone/settings`),
      ]);
      setAnalytics(a.data);
      setSettings(s.data);
      if (s.data?.public_host) setRewireHost(s.data.public_host);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [range]);

  const platform = analytics?.platform || {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const answerRate = useMemo(() => {
    const answered = platform.inbound_answered || 0;
    const missed = platform.inbound_missed || 0;
    const total = answered + missed;
    if (!total) return "—";
    return `${Math.round((answered / total) * 100)}%`;
  }, [platform.inbound_answered, platform.inbound_missed]);

  const submitRewire = async () => {
    if (!rewireHost.startsWith("https://")) {
      toast.error("Host must start with https://");
      return;
    }
    if (!window.confirm(`This will point ALL Twilio webhooks (voice + SMS + status) at:\n${rewireHost}\n\nContinue?`)) return;
    setRewireSaving(true);
    try {
      const { data } = await api.post("/admin/phone/rewire", { host: rewireHost });
      toast.success(`Webhooks rewired — TwiML=${data.twiml_app_updated ? "✓" : "×"}, Number=${data.number_updated ? "✓" : "×"}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setRewireSaving(false); }
  };

  const manualProvision = async () => {
    if (!window.confirm("Run Twilio provisioning now? This is idempotent — it only creates what's missing.")) return;
    try {
      const { data } = await api.post("/admin/phone/provision");
      toast.success(`Provisioning complete. Number=${data.phone_number || "unchanged"}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AdminPage
      title="Phone system"
      subtitle="Twilio-powered inbound/outbound calls, SMS, voicemail, and BDC softphone analytics."
      testId="admin-phone-page"
      action={
        <div className="flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-28" data-testid="admin-phone-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="ytd">Year to date</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSettingsOpen(true)}
            data-testid="admin-phone-settings-btn"
          >
            <Settings size={13} /> Settings
          </Button>
        </div>
      }
    >
      {loading || !analytics ? (
        <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" />
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="admin-phone-kpis">
            <KpiCard label="Total calls" value={platform.total_calls || 0} icon={Phone} testId="admin-phone-kpi-total" />
            <KpiCard label="Inbound answered" value={platform.inbound_answered || 0} tone="emerald" icon={PhoneIncoming} testId="admin-phone-kpi-answered" />
            <KpiCard label="Missed" value={platform.inbound_missed || 0} tone="rose" icon={PhoneMissed} testId="admin-phone-kpi-missed" />
            <KpiCard label="Answer rate" value={answerRate} tone="brand" icon={CheckCircle2} testId="admin-phone-kpi-answer-rate" />
            <KpiCard label="Outbound placed" value={platform.outbound_placed || 0} icon={PhoneOutgoing} testId="admin-phone-kpi-outbound" />
            <KpiCard label="Voicemails" value={platform.voicemails || 0} tone="gold" icon={Voicemail} testId="admin-phone-kpi-voicemail" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <KpiCard label="SMS sent" value={platform.sms_sent || 0} icon={MessageSquare} testId="admin-phone-kpi-sms-sent" />
            <KpiCard label="SMS received" value={platform.sms_received || 0} icon={MessageSquare} testId="admin-phone-kpi-sms-received" />
            <KpiCard label="Avg handle time" value={formatDuration(platform.avg_handle_time_seconds || 0)} icon={Phone} testId="admin-phone-kpi-avg-handle" />
            <KpiCard label="Agents online" value={settings?.agents_online || 0} tone="emerald" icon={Wifi} testId="admin-phone-kpi-agents-online" />
          </div>

          {/* Time series chart */}
          <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-4" data-testid="admin-phone-timeseries">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Call volume by day</p>
                <p className="font-display text-lg font-semibold text-slate-900">Inbound · Outbound · Missed</p>
              </div>
            </div>
            <div className="h-72">
              {analytics.time_series?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.time_series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="inbound" stackId="a" fill="#1B3A6B" name="Inbound" />
                    <Bar dataKey="outbound" stackId="a" fill="#D4A537" name="Outbound" />
                    <Bar dataKey="missed" stackId="a" fill="#e11d48" name="Missed" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-slate-400">
                  No calls in this range yet.
                </div>
              )}
            </div>
          </div>

          {/* Per-agent leaderboard */}
          <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="admin-phone-agents">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">BDC agents · call activity</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Agent</th>
                  <th className="text-right px-4 py-2 font-semibold">Answered</th>
                  <th className="text-right px-4 py-2 font-semibold">Missed</th>
                  <th className="text-right px-4 py-2 font-semibold">Outbound</th>
                  <th className="text-right px-4 py-2 font-semibold">Talk time</th>
                  <th className="text-right px-4 py-2 font-semibold">Avg handle</th>
                </tr>
              </thead>
              <tbody>
                {(analytics.per_agent || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-sm text-slate-400 px-4 py-6">
                      No agent activity in this range.
                    </td>
                  </tr>
                ) : analytics.per_agent.map((a) => (
                  <tr key={a.bdc_agent_id} className="border-t border-slate-100" data-testid={`admin-phone-agent-row-${a.bdc_agent_id}`}>
                    <td className="px-4 py-2 font-medium">
                      {a.full_name || a.email || "—"}
                      {a.email && <div className="text-[10px] text-slate-400">{a.email}</div>}
                    </td>
                    <td className="px-4 py-2 text-right text-emerald-700 font-semibold">{a.calls_answered}</td>
                    <td className="px-4 py-2 text-right text-rose-700">{a.calls_missed}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{a.outbound_placed}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{formatDuration(a.talk_time_seconds || 0)}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{formatDuration(a.avg_handle_time_seconds || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recent voicemails */}
          <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-4" data-testid="admin-phone-voicemails">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Recent voicemails</p>
            {(analytics.recent_voicemails || []).length === 0 ? (
              <p className="text-sm text-slate-400 mt-3">No voicemails in this window.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {analytics.recent_voicemails.slice(0, 6).map((vm) => (
                  <li key={vm.id} className="border-l-2 border-gold pl-3" data-testid={`admin-vm-${vm.id}`}>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Voicemail size={12} className="text-gold" />
                      <span className="font-semibold text-slate-800">{formatPhoneDisplay(vm.from_number)}</span>
                      <span>· {relativeTime(vm.started_at)}</span>
                      <span>· {formatDateTime(vm.started_at)}</span>
                    </div>
                    {vm.voicemail_transcript && (
                      <p className="text-sm text-slate-700 mt-1 italic">"{vm.voicemail_transcript.slice(0, 200)}"</p>
                    )}
                    {vm.recording_url && (
                      <audio controls src={vm.recording_url} className="mt-2 w-full max-w-md" data-testid={`admin-vm-audio-${vm.id}`} />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-phone-settings-dialog">
          <DialogHeader>
            <DialogTitle>Phone system settings</DialogTitle>
            <DialogDescription>
              Twilio provisioning + webhook host. Rewire after promoting to a new production domain.
            </DialogDescription>
          </DialogHeader>
          {!settings ? (
            <div className="h-40 bg-slate-100 animate-pulse rounded-lg" />
          ) : (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">BDC number</p>
                  <p className="font-mono font-semibold text-slate-900" data-testid="admin-phone-number-value">
                    {formatPhoneDisplay(settings.phone_number) || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Account type</p>
                  <p className="font-semibold text-slate-900">{settings.account_type || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">TwiML app SID</p>
                  <p className="font-mono text-xs text-slate-700">{settings.twiml_app_sid || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">API key prefix</p>
                  <p className="font-mono text-xs text-slate-700">{settings.api_key_sid_prefix ? `${settings.api_key_sid_prefix}…` : "—"}</p>
                </div>
              </div>

              {settings.account_type === "Trial" && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="admin-phone-trial-notice">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Twilio Trial mode</p>
                    <p>Outbound calls/SMS only reach verified caller IDs. Upgrade at twilio.com to lift restrictions.</p>
                    {settings.verified_caller_ids?.length > 0 && (
                      <p className="mt-1">Verified: {settings.verified_caller_ids.map((v) => v.phone_number).join(", ")}</p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="rewire-host" className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                  Webhook host
                </Label>
                <p className="text-xs text-slate-500 mb-2">
                  Full https URL — Twilio will POST to <code className="font-mono">{"{host}/api/twilio/*"}</code>.
                </p>
                <Input
                  id="rewire-host"
                  value={rewireHost}
                  onChange={(e) => setRewireHost(e.target.value)}
                  placeholder="https://belomotors.com"
                  className="font-mono text-sm"
                  data-testid="admin-phone-rewire-host-input"
                />
                <div className="flex items-center justify-between gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={manualProvision} data-testid="admin-phone-provision-btn">
                    <RefreshCw size={13} /> Re-run provisioning
                  </Button>
                  <Button
                    size="sm"
                    className="bg-brand hover:bg-brand/90 text-white"
                    disabled={rewireSaving || !rewireHost}
                    onClick={submitRewire}
                    data-testid="admin-phone-rewire-submit-btn"
                  >
                    {rewireSaving ? "Rewiring…" : "Rewire webhooks"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettingsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default AdminPhone;
